import { designService } from '@paragon/data-connections';
import { editLayoutModelProperties, saveAllProjectChanges } from '@paragon/layout-models';

import type { RootState } from '../../store';
import { setRenderModeAndEnsureSelectedItemIsAppropriate } from '../../store/actions/cameraActions';
import { redo, undo } from '../../store/actions/editorActions';
import { createBuildingAndSaveProject, createLevelAndSaveProject } from '../../store/actions/layout/hierarchyActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getAllBuildings, getAllLevels } from '../../store/selectors/layoutDataSelectors';
import { isLayoutModeEnabled } from '../../store/selectors/layoutModeSelectors';
import { getChat } from '../assistantStore';
import { findLevelByName, shortId } from './contextTools';
import { openTruss, requireProject, requireString, summarizeTruss, waitForAppToSettle } from './helpers';
import { defineTool, ToolError } from './types';

type LevelProperties = {
  readonly name?: string;
  readonly plateElevation?: number;
  readonly wallHeight?: number;
  readonly floorHeight?: number;
};

const LEVEL_PROPERTIES_SCHEMA = {
  name: { type: 'string' },
  plateElevation: { type: 'number', description: 'Inches. Where the level’s walls top out and its trusses bear.' },
  wallHeight: { type: 'number', description: 'Inches.' },
  floorHeight: { type: 'number', description: 'Inches. The floor system’s depth.' },
};

export function pickLevelProperties(input: LevelProperties) {
  const properties: Record<string, string | number> = {};
  if (input.name != null) {
    if (input.name.trim() === '') throw new ToolError('A level’s name can’t be empty.');
    properties.name = input.name.trim();
  }
  for (const key of ['plateElevation', 'wallHeight', 'floorHeight'] as const) {
    const value = input[key];
    if (value == null) continue;
    if (!Number.isFinite(value) || value < 0) throw new ToolError(`${key} must be a number of inches, 0 or more.`);
    properties[key] = value;
  }
  return properties;
}

const LAYOUT_COLLECTIONS = {
  walls: 'bearingEnvelopes',
  trussEnvelopes: 'trussEnvelopes',
  roofPlanes: 'roofPlanes',
  workLines: 'workLines',
  floorAreas: 'featurePatternContainers',
} as const;

/**
 * Which layout objects a step added, removed, or changed, by name (roof planes by id).
 */
function diffLayout(before: RootState['layout'], after: RootState['layout']) {
  const label = (item: { guid: string; name?: string }) => item.name ?? shortId(item.guid);
  const result: Record<string, { added?: string[]; removed?: string[]; changed?: string[] }> = {};
  for (const [kind, key] of Object.entries(LAYOUT_COLLECTIONS)) {
    const old = before[key] as Record<string, { guid: string; name?: string }>;
    const now = after[key] as Record<string, { guid: string; name?: string }>;
    const added = Object.values(now)
      .filter((item) => !(item.guid in old))
      .map(label);
    const removed = Object.values(old)
      .filter((item) => !(item.guid in now))
      .map(label);
    const changed = Object.values(now)
      .filter((item) => item.guid in old && old[item.guid] !== item)
      .map(label);
    if (added.length + removed.length + changed.length > 0)
      result[kind] = {
        ...(added.length > 0 ? { added } : {}),
        ...(removed.length > 0 ? { removed } : {}),
        ...(changed.length > 0 ? { changed } : {}),
      };
  }
  return result;
}

export const projectTools = [
  defineTool<{ building?: string } & LevelProperties>({
    name: 'add_level',
    description:
      'Adds a level to a building, as the navigation panel’s Add Level does, and sets its name and elevations. A ' +
      'project with no building gets one first, which also moves everything already in the layout onto its level.',
    inputSchema: {
      type: 'object',
      properties: {
        building: { type: 'string', description: 'The building’s name. Defaults to the only building.' },
        ...LEVEL_PROPERTIES_SCHEMA,
      },
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      requireProject(getState());
      const properties = pickLevelProperties(input);
      const levelsBefore = new Set(getAllLevels(getState()).map((level) => level.guid));

      const buildings = getAllBuildings(getState());
      if (buildings.length === 0) {
        await dispatch(createBuildingAndSaveProject());
      } else {
        const building =
          input.building != null
            ? buildings.find(
                (candidate) => candidate.name.trim().toLowerCase() === input.building!.trim().toLowerCase(),
              )
            : buildings.length === 1
              ? buildings[0]
              : undefined;
        if (building == null)
          throw new ToolError(
            input.building != null
              ? `There is no building named "${input.building}".`
              : `Say which building: ${buildings.map((candidate) => candidate.name).join(', ')}.`,
          );
        await dispatch(createLevelAndSaveProject(building.guid));
      }
      await waitForAppToSettle(getState);

      const level = getAllLevels(getState()).find((candidate) => !levelsBefore.has(candidate.guid));
      if (level == null) throw new ToolError('The app could not add the level.');

      if (Object.keys(properties).length > 0) {
        dispatch(editLayoutModelProperties(level.guid, properties));
        await dispatch(saveAllProjectChanges());
        await waitForAppToSettle(getState);
      }

      const saved = getAllLevels(getState()).find((candidate) => candidate.guid === level.guid)!;
      return {
        summary: {
          added: saved.name,
          plateElevation: saved.plateElevation,
          wallHeight: saved.wallHeight,
          floorHeight: saved.floorHeight,
          levels: getAllLevels(getState()).map((candidate) => candidate.name),
        },
      };
    },
  }),

  defineTool<{ level: string } & LevelProperties>({
    name: 'edit_level',
    description:
      'Renames a level or changes its elevations (inches), as its Edit Properties panel does. New walls and roof ' +
      'planes on the level use these heights.',
    inputSchema: {
      type: 'object',
      properties: { level: { type: 'string', description: 'The level’s current name.' }, ...LEVEL_PROPERTIES_SCHEMA },
      required: ['level'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      requireProject(getState());
      const level = findLevelByName(getState(), requireString(input, 'level'));
      const properties = pickLevelProperties(input);
      if (Object.keys(properties).length === 0) throw new ToolError('Give a name or an elevation to change.');

      dispatch(editLayoutModelProperties(level.guid, properties));
      await dispatch(saveAllProjectChanges());
      await waitForAppToSettle(getState);

      const saved = getAllLevels(getState()).find((candidate) => candidate.guid === level.guid)!;
      return {
        summary: {
          level: saved.name,
          plateElevation: saved.plateElevation,
          wallHeight: saved.wallHeight,
          floorHeight: saved.floorHeight,
        },
      };
    },
  }),

  defineTool<{ action: 'undo' | 'redo'; truss?: string }>({
    name: 'undo',
    description:
      'Undoes or redoes the last change, as Ctrl+Z and Ctrl+Y do. Give a truss to undo its edits; otherwise it ' +
      'undoes layout changes. The app can’t undo truss creation or deletion, load settings, or jack-girder ' +
      'connections, and a truss generated from an envelope stays after its envelope change is undone.',
    inputSchema: {
      type: 'object',
      properties: {
        action: { type: 'string', enum: ['undo', 'redo'] },
        truss: { type: 'string', description: 'Undo this truss’s edits instead of the layout’s.' },
      },
      required: ['action'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      if (input.action !== 'undo' && input.action !== 'redo') throw new ToolError('"action" must be undo or redo.');

      if (input.truss != null) {
        await openTruss(dispatch, getState, input.truss);
      } else {
        if (!isLayoutModeEnabled(getState())) throw new ToolError('This project has no layout; name a truss.');
        dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate('layout'));
        await waitForAppToSettle(getState);
      }

      const chain = input.truss != null ? null : getState().layout.undoRedoChain;
      const before = chain == null ? null : input.action === 'undo' ? chain.undo : chain.redo;
      if (chain != null && before == null) throw new ToolError(`There is nothing to ${input.action} in the layout.`);

      const truss = input.truss != null ? getActiveComponentDesign(getState()) : null;
      const layoutBefore = getState().layout;
      await dispatch(input.action === 'undo' ? undo() : redo());
      await waitForAppToSettle(getState);

      // Say what the step changed, so the Assistant knows what it took back without reading everything again.
      if (truss != null) {
        const after = getState().componentDesigns.data[truss.guid];
        return {
          summary: {
            done: input.action,
            in: truss.component.name,
            changed: after != null && after !== truss,
            truss: after != null ? summarizeTruss(after) : null,
          },
        };
      }
      return { summary: { done: input.action, in: 'layout', changed: diffLayout(layoutBefore, getState().layout) } };
    },
  }),

  defineTool<{ request: string; missing: string }>({
    name: 'report_missing_capability',
    description:
      'Records that the designer asked for something none of your tools can do, so the team can add it. Call it ' +
      'whenever that happens, then tell the designer how to do it in the app themselves, and that Show me how, ' +
      'under your reply, records them doing it for the team.',
    inputSchema: {
      type: 'object',
      properties: {
        request: { type: 'string', description: 'What the designer asked for, in their words.' },
        missing: { type: 'string', description: 'The app action you would have needed.' },
      },
      required: ['request', 'missing'],
    },
    changesProject: false,
    async execute(input, { caller, chatId }) {
      const request = requireString(input, 'request');
      const missing = requireString(input, 'missing');
      try {
        await designService.assistant.reportMissingCapability({
          conversationGuid: chatId == null ? null : (getChat(chatId)?.conversationGuid ?? null),
          caller,
          request,
          missing,
        });
      } catch {
        throw new ToolError('It couldn’t be recorded. Tell the designer how to do it in the app themselves.');
      }
      return { summary: { recorded: true, missing } };
    },
  }),
];
